import type { EntityState, SerializedError } from '@reduxjs/toolkit';
import z from 'zod';

export const RECEIPTS_REDUCER_KEY = 'receiptsStore';

/** How often consumers should re-fetch while any receipt is still in flight. */
export const RECEIPTS_POLL_INTERVAL_MS = 10_000;

/** Maximum receipt page size requested by automatic full and incremental syncs. */
export const RECEIPTS_DEFAULT_PAGE_SIZE = 1_000;

/**
 * The receipt service status set. Locally created receipts reuse these statuses while they move
 * through the device upload flow (UPLOAD_PENDING while the file uploads, SCANNING once the receipt
 * resource is being created, FAILED with an UPLOAD_FAILED/EXTRACTION_FAILED subStatus on failure).
 * UNKNOWN is client-only: statuses the server adds later parse as UNKNOWN instead of failing the
 * whole response.
 */
const RECEIPT_STATUSES = [
  'UPLOAD_PENDING',
  'UPLOADED',
  'SCANNING',
  'READY',
  'FAILED',
  'UNPROCESSABLE',
  'UNKNOWN',
] as const;

const isKnownReceiptStatus = (value: string): value is ReceiptStatus =>
  (RECEIPT_STATUSES as readonly string[]).includes(value);

export const ReceiptStatusSchema = z.preprocess(
  (value) => (typeof value === 'string' && !isKnownReceiptStatus(value) ? 'UNKNOWN' : value),
  z.enum(RECEIPT_STATUSES),
);
export type ReceiptStatus = (typeof RECEIPT_STATUSES)[number];

/**
 * Sub-statuses attached to FAILED (retryable) and UNPROCESSABLE (permanent) receipts.
 * CREATION_FAILED and UNKNOWN are client-only: the former distinguishes a locally unreconciled
 * create request, while server sub-statuses added later parse as UNKNOWN instead of failing the
 * whole response.
 */
const RECEIPT_SUB_STATUSES = [
  'UPLOAD_FAILED',
  'UPLOAD_EXPIRED',
  'MALWARE_DETECTED',
  'QUOTA_EXCEEDED',
  'STORAGE_NOT_READY',
  'CREATION_FAILED',
  'EXTRACTION_FAILED',
  'NOT_A_RECEIPT',
  'UNSUPPORTED_FORMAT',
  'UNREADABLE',
  'UNKNOWN',
] as const;

const isKnownReceiptSubStatus = (value: string): value is ReceiptSubStatus =>
  (RECEIPT_SUB_STATUSES as readonly string[]).includes(value);

export const ReceiptSubStatusSchema = z.preprocess(
  (value) => (typeof value === 'string' && !isKnownReceiptSubStatus(value) ? 'UNKNOWN' : value),
  z.enum(RECEIPT_SUB_STATUSES),
);
export type ReceiptSubStatus = (typeof RECEIPT_SUB_STATUSES)[number];

export enum ReceiptFeedbackRating {
  POSITIVE = 'POSITIVE',
  NEGATIVE = 'NEGATIVE',
}

export const ReceiptFeedbackRatingSchema = z.enum([ReceiptFeedbackRating.POSITIVE, ReceiptFeedbackRating.NEGATIVE]);

export const RECEIPT_FEEDBACK_REASON_ORDER = [
  'PAYEE_NAME',
  'DATE',
  'RECEIPT_TOTAL',
  'ITEMS',
  'SUBTOTAL_TIPS_TAXES_DISCOUNTS',
  'OTHER',
] as const;

export const ReceiptFeedbackReasonSchema = z.enum(RECEIPT_FEEDBACK_REASON_ORDER);
export type ReceiptFeedbackReason = z.infer<typeof ReceiptFeedbackReasonSchema>;

export const ReceiptFeedbackSchema = z.object({
  rating: ReceiptFeedbackRatingSchema,
  reasons: z.array(ReceiptFeedbackReasonSchema),
  comment: z.string().nullable().default(null),
  updatedAt: z.string(),
});
export type ReceiptFeedback = z.infer<typeof ReceiptFeedbackSchema>;

const normalizeScannedReceiptWhitespace = (value: string): string => value.replace(/\s+/g, ' ').trim().toLowerCase();

const normalizeScannedReceiptSentenceCase = (value: string): string => {
  const normalizedWhitespace = normalizeScannedReceiptWhitespace(value);
  const [firstCharacter = '', ...remainingCharacters] = [...normalizedWhitespace];

  return `${firstCharacter.toUpperCase()}${remainingCharacters.join('')}`;
};

const normalizeScannedReceiptMerchant = (value: string): string =>
  normalizeScannedReceiptWhitespace(value)
    .split(' ')
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(' ');

const ScannedReceiptSentenceCaseSchema = z.string().transform(normalizeScannedReceiptSentenceCase);
const ScannedReceiptMerchantSchema = z.string().transform(normalizeScannedReceiptMerchant);

export const ReceiptLineItemSchema = z.object({
  description: ScannedReceiptSentenceCaseSchema.optional(),
  quantity: z.number().optional(),
  amount: z.number().optional(),
});
export type ReceiptLineItem = z.infer<typeof ReceiptLineItemSchema>;

export const ReceiptExtractionSchema = z
  .object({
    merchant: ScannedReceiptMerchantSchema.optional(),
    transactionDate: z.string().optional(),
    total: z.number().optional(),
    subtotal: z.number().optional(),
    tax: z.number().optional(),
    tip: z.number().optional(),
    currency: z.string().optional(),
    paymentMethod: ScannedReceiptSentenceCaseSchema.optional(),
    lastFour: z.string().optional(),
    suggestedCategory: ScannedReceiptSentenceCaseSchema.optional(),
    lineItems: z.array(ReceiptLineItemSchema).optional(),
    confidence: z.number().optional(),
    rawText: z.string().optional(),
  })
  .passthrough();
export type ReceiptExtraction = z.infer<typeof ReceiptExtractionSchema>;

export const ReceiptConfidencesSchema = z.record(z.number());

const UploadedByUserIdSchema = z.preprocess((value) => {
  if (typeof value === 'number') {
    return String(value);
  }

  return value;
}, z.string());

const ReceiptResourceSchema = z
  .object({
    id: z.string(),
    documentId: z.string(),
    name: z.string().nullish(),
    status: ReceiptStatusSchema,
    subStatus: ReceiptSubStatusSchema.optional(),
    merchant: ScannedReceiptMerchantSchema.optional(),
    total: z.number().optional(),
    currency: z.string().optional(),
    extractionEngine: z.string().optional(),
    extraction: ReceiptExtractionSchema.optional(),
    confidences: ReceiptConfidencesSchema.optional(),
    transactionDate: z.string().nullish(),
    transactionId: z.string().nullish(),
    memo: z.string().optional(),
    errors: z.array(z.string()).optional(),
    isDeleted: z.boolean().optional(),
    uploadedByUserId: UploadedByUserIdSchema.optional(),
    createdDate: z.string().optional(),
    createdAt: z.string().optional(),
    modifiedDate: z.string().optional(),
    modifiedAt: z.string().optional(),
    viewedAt: z.string().nullish(),
    feedback: ReceiptFeedbackSchema.nullish(),
  })
  .superRefine((receipt, context) => {
    if (!receipt.createdDate && !receipt.createdAt) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Required',
        path: ['createdDate'],
      });
    }

    if (!receipt.modifiedDate && !receipt.modifiedAt) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Required',
        path: ['modifiedDate'],
      });
    }
  })
  .transform(({ createdAt, modifiedAt, transactionDate, transactionId, ...receipt }) => {
    const extraction: ReceiptExtraction = {
      ...receipt.extraction,
      merchant: receipt.merchant ?? receipt.extraction?.merchant,
      transactionDate: transactionDate ?? receipt.extraction?.transactionDate,
      total: receipt.total ?? receipt.extraction?.total,
      currency: receipt.currency ?? receipt.extraction?.currency,
    };
    const hasExtraction = Object.values(extraction).some((value) => value !== undefined);

    return {
      ...receipt,
      name: receipt.name ?? '',
      createdDate: receipt.createdDate ?? createdAt ?? '',
      modifiedDate: receipt.modifiedDate ?? modifiedAt ?? '',
      ...(transactionDate !== null && transactionDate !== undefined ? { transactionDate } : {}),
      ...(transactionId !== null && transactionId !== undefined ? { transactionId } : {}),
      ...(hasExtraction ? { extraction } : {}),
    };
  });

export const ReceiptSchema = ReceiptResourceSchema;

export type ReceiptResource = z.infer<typeof ReceiptSchema>;

export type Receipt = ReceiptResource & {
  /** Client-owned destination until a successful link response confirms the service-owned transactionId. */
  targetTransactionId?: string;
  /** A link attempt failed; retry the existing receipt rather than uploading it again. */
  linkFailed?: boolean;
  isLinking?: boolean;
  /** Its editor's Update is saving; no other editor may release it. */
  linkQueued?: boolean;
  localFileUri?: string;
  mimeType?: string;
  /** Preserves picker order while a local upload is replaced by service-owned receipt data. */
  localUploadOrder?: {
    batchCreatedAt: number;
    batchIndex: number;
  };
  /** A local placeholder is visible, but its upload pipeline has not started yet. */
  isUploadQueued?: boolean;
  /** The create request had an ambiguous result, so full syncs must reconcile this local row by document id. */
  needsReconciliation?: boolean;
};

export type MarkReceiptCreationFailedPayload = {
  id: string;
  documentId: string;
  documentName: string;
  mimeType: string;
};

export type MarkReceiptCreationPendingPayload = {
  id: string;
  documentId: string;
  documentName: string;
  mimeType: string;
};

export type ExpireStalePendingReceiptsPayload = {
  now: number;
  /** Ids the client is still actively uploading; the local deadline must not expire them. */
  activeUploadIds?: ReadonlyArray<string>;
};

export type AddPendingReceiptPayload = {
  id: string;
  fileUri: string;
  fileName: string;
  mimeType: string;
  createdAt: number;
  localUploadOrder?: Receipt['localUploadOrder'];
  isUploadQueued?: boolean;
  targetTransactionId?: string;
};

export type MarkReceiptUploadStartedPayload = {
  id: string;
  now: number;
};

export type CreateReceiptPayload = {
  documentId: string;
  name?: string | null;
};

export type UpdateReceiptPayload = {
  id: string;
  merchant?: string;
  transactionDate?: string;
  total?: number;
  currency?: string;
  memo?: string;
};

export type SubmitReceiptFeedbackPayload =
  | {
      id: string;
      rating: ReceiptFeedbackRating.POSITIVE;
      reasons?: never;
      comment?: never;
    }
  | {
      id: string;
      rating: ReceiptFeedbackRating.NEGATIVE;
      reasons?: ReceiptFeedbackReason[];
      comment?: string;
    };

export type FetchReceiptsParams = {
  modifiedAfter?: string;
  page?: number;
  size?: number;
  sort?: string;
  status?: Exclude<ReceiptStatus, 'UNKNOWN'>;
  transactionId?: string;
};

export type FetchReceiptsResponse = {
  resources: Receipt[];
  metaData?: {
    pageSize?: number;
    limit?: number;
    asOf?: string;
    lastRefId?: string;
    currentPage?: number;
    offset?: number;
    totalPages?: number;
    totalSize?: number;
  };
  totalElements: number;
  totalPages: number;
};

export type LinkReceiptToTransactionPayload = {
  id: string;
  transactionId: string;
};

export type ReceiptReviewState = 'NEEDS_REVIEW' | 'REVIEWED' | 'PROCESSING_OR_ERROR';
export type ReceiptPageSize = 25 | 50 | 100;
export type ReceiptPageSortField = 'uploadedDate' | 'effectiveDate' | 'name' | 'amount';
export type ReceiptPageSort = { field: ReceiptPageSortField; direction: 'asc' | 'desc' };

/** Pages are zero-based in client state; only the API boundary converts them. */
export type ReceiptPageParams = {
  reviewState: ReceiptReviewState;
  page: number;
  pageSize: ReceiptPageSize;
  sort: ReceiptPageSort;
};
export type ReceiptPageQuery = Omit<ReceiptPageParams, 'reviewState'> & {
  ids: string[];
  total: number;
  isLoading: boolean;
  loaded: boolean;
  revision: number;
  requestId?: string;
  error: SerializedError | null;
};
export const ReceiptSummarySchema = z.object({
  needsReview: z.number().int().nonnegative(),
  reviewed: z.number().int().nonnegative(),
  processing: z.number().int().nonnegative(),
  processingErrors: z.number().int().nonnegative(),
});
export type ReceiptSummary = z.infer<typeof ReceiptSummarySchema>;
export type ReceiptCenterQueries = {
  enabled: boolean;
  activeReviewState: 'NEEDS_REVIEW' | 'REVIEWED';
  pages: Record<ReceiptReviewState, ReceiptPageQuery>;
  summary: ReceiptSummary | null;
  summaryRequestId?: string;
  /** Newly registered uploads remain counted until a subsequent summary acknowledges them. */
  summaryPendingReceiptIds?: string[];
  summaryRequestReceiptIds?: string[];
  summaryError: SerializedError | null;
  revision: number;
};

/** transactionId is null once detached or deleted; version orders changes against an editor's baseline. */
export type LocalLinkChange = {
  transactionId: string | null;
  version: number;
  /** Kept so a deleted receipt's attachment can still be matched by its document id. */
  documentId?: string;
};

export type ReceiptsState = EntityState<Receipt, string> & {
  error?: SerializedError | null;
  isCreating: boolean;
  isLoading: boolean;
  sessionEpoch: number;
  lastSyncDate: string | null;
  loadPending: boolean;
  totalElements: number;
  totalPages: number;
  center?: ReceiptCenterQueries;
  /** By receipt id; open editors treat changes made after their baseline as their own. */
  localLinkChanges: Record<string, LocalLinkChange>;
  /** Latest localLinkChanges version; never reused, even after entries are dropped. */
  localLinkVersion: number;
};

declare module 'app-components/utils/redux-store/rootState' {
  export default interface RootState {
    [RECEIPTS_REDUCER_KEY]: ReceiptsState;
  }
}
